import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ShieldCheck,
    Search,
    UserCheck,
    Users,
    FolderGit2,
    BrainCircuit,
    Shield,
} from "lucide-react";
import TopNav from "../components/TopNav";
import SubscriptionsTab from "../components/admin/SubscriptionsTab";
import FutureTab from "../components/admin/FutureTab";
import SettingsTab from "../components/admin/SettingsTab";
import DisplayTab from "../components/admin/DisplayTab";
import KeywordsTab from "../components/admin/KeywordsTab";
import WorkgroupsTab from "../components/admin/WorkgroupsTab";
import SecurityTab from "../components/admin/SecurityTab";
import WebsiteTab from "../components/admin/WebsiteTab";
import { BrandingSection } from "../components/admin/BrandingSection";
import { IntegrationsSection } from "../components/admin/IntegrationsSection";
import { ClientErrorsSection } from "../components/admin/ClientErrorsSection";
import { TemplatesInjectionPromptSection } from "../components/admin/TemplatesInjectionPromptSection";
import { PricingStrategiesSection } from "../components/admin/PricingStrategiesSection";
import { UsersTable } from "../components/admin/UsersTable";
import { ProjectsTable } from "../components/admin/ProjectsTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../lib/auth";
import { BUILD_INFO } from "../build-info";
import {
    adminStats,
    adminUsers,
    adminProjects,
    adminImpersonate,
    adminGetReviewerStatus,
    adminSetReviewerStatus,
} from "../lib/api";

/**
 * Superadmin dashboard (iter-42). Only accessible when
 * ``user.role === "superadmin"`` — rendered otherwise as a redirect to
 * the homepage so we don't leak the existence of the page.
 *
 * Shows:
 *   - Global stat tiles (users, projects, analyses, estimates, iQ spend)
 *   - Searchable users table with per-user iQ spend + project count
 *   - "Impersonate" button that swaps the current session to the target
 *     user (original admin token stashed in ``pf_admin_return_token``)
 *   - Projects table with owner info
 */

const TOKEN_KEY = "planflow.auth_token";
const USER_KEY = "planflow.auth_user";
const RETURN_TOKEN_KEY = "pf_admin_return_token";
const RETURN_USER_KEY = "pf_admin_return_user";

export default function AdminPage() {
    const { user, loading } = useAuth();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [tab, setTab] = useState("users"); // "users" | "projects"
    const [q, setQ] = useState("");
    const [loadingData, setLoadingData] = useState(true);

    useEffect(() => {
        if (!user || !["superadmin", "reviewer"].includes(user.role)) return;
        let cancelled = false;
        (async () => {
            setLoadingData(true);
            try {
                const [s, u, p] = await Promise.all([
                    adminStats(),
                    adminUsers(),
                    adminProjects(),
                ]);
                if (cancelled) return;
                setStats(s);
                setUsers(u);
                setProjects(p);
            } catch (err) {
                toast.error(
                    err?.response?.data?.detail || "Couldn't load admin data",
                );
            } finally {
                if (!cancelled) setLoadingData(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user]);

    const filteredUsers = useMemo(() => {
        const qq = q.trim().toLowerCase();
        if (!qq) return users;
        return users.filter((x) => {
            const hay =
                (x.email || "") +
                " " +
                (x.full_name || "") +
                " " +
                (x.company || "") +
                " " +
                (x.user_id || "");
            return hay.toLowerCase().includes(qq);
        });
    }, [q, users]);

    const filteredProjects = useMemo(() => {
        const qq = q.trim().toLowerCase();
        if (!qq) return projects;
        return projects.filter((x) =>
            (
                (x.name || "") +
                " " +
                (x.owner_email || "") +
                " " +
                (x.owner_name || "") +
                " " +
                (x.id || "")
            )
                .toLowerCase()
                .includes(qq),
        );
    }, [q, projects]);

    const onImpersonate = async (target) => {
        if (!window.confirm(
            `Impersonate ${target.email || target.user_id}?\n\nYour admin session token will be saved — click "Return to superadmin" in the banner to come back.`,
        )) {
            return;
        }
        try {
            const res = await adminImpersonate(target.user_id);
            // Stash admin creds to return to later.
            const currentToken = localStorage.getItem(TOKEN_KEY);
            const currentUser = localStorage.getItem(USER_KEY);
            if (currentToken) localStorage.setItem(RETURN_TOKEN_KEY, currentToken);
            if (currentUser) localStorage.setItem(RETURN_USER_KEY, currentUser);
            // Swap to the impersonated user.
            localStorage.setItem(TOKEN_KEY, res.access_token);
            localStorage.setItem(
                USER_KEY,
                JSON.stringify({
                    ...res.target_user,
                    // Keep auth_provider so nothing in the app trips up.
                    auth_provider: "local",
                }),
            );
            toast.success(`Signed in as ${res.target_user.email || "user"}`);
            // Hard reload so every context reads the new token.
            window.location.href = "/";
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Impersonation failed",
            );
        }
    };

    if (loading) {
        return (
            <div
                className="h-[100dvh] w-full overflow-y-auto bg-[#0A1128] text-slate-200 flex items-center justify-center"
                data-testid="admin-loading"
            >
                Loading…
            </div>
        );
    }
    if (!user || !["superadmin", "reviewer"].includes(user.role)) {
        return <Navigate to="/" replace />;
    }

    return (
        <div
            className="h-[100dvh] w-full overflow-y-auto bg-[#0A1128] text-slate-100 flex flex-col"
            data-testid="admin-page"
        >
            <TopNav />
            <main className="flex-1 px-4 sm:px-8 py-6 sm:py-8 pb-44 max-w-7xl mx-auto w-full">
                <header className="mb-6 flex items-end justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-rose-400 font-bold">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            {user.role === "reviewer" ? "Reviewer" : "Superadmin"}
                        </div>
                        <h1 className="mt-1 text-3xl sm:text-4xl font-black tracking-tight text-white">
                            Control room
                        </h1>
                        {user.role === "reviewer" && (
                            <p
                                className="mt-2 text-[12px] text-amber-300/90 max-w-prose"
                                data-testid="admin-reviewer-banner"
                            >
                                Reviewer mode — full read access + unlimited AI credits. Integration keys and AI prompts are read-only.
                            </p>
                        )}
                        <div
                            className="mt-3 inline-flex flex-wrap items-center gap-2 text-[10px] text-slate-500 font-mono"
                            data-testid="admin-build-info"
                            title={`Built ${BUILD_INFO.builtAt || "(unknown)"} on branch ${BUILD_INFO.branch}`}
                        >
                            <span className="px-1.5 py-0.5 rounded-sm bg-[#0F1B3A] border border-[#2A3655]">
                                build {BUILD_INFO.commit}
                            </span>
                            {BUILD_INFO.builtAt && (
                                <span className="text-slate-600">
                                    {(() => {
                                        try {
                                            return new Date(BUILD_INFO.builtAt).toLocaleString();
                                        } catch {
                                            return BUILD_INFO.builtAt;
                                        }
                                    })()}
                                </span>
                            )}
                        </div>
                    </div>
                </header>

                {/* Stat tiles */}
                <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                    <StatTile
                        icon={Users}
                        label="Users"
                        value={stats?.users ?? "—"}
                    />
                    <StatTile
                        icon={FolderGit2}
                        label="Projects"
                        value={stats?.projects ?? "—"}
                    />
                    <StatTile
                        icon={BrainCircuit}
                        label="Analyses"
                        value={stats?.analyses ?? "—"}
                    />
                    <StatTile
                        icon={BrainCircuit}
                        label="Estimates"
                        value={stats?.estimates ?? "—"}
                    />
                    {/* iter-62: Workgroups count (red box request). */}
                    <StatTile
                        icon={Users}
                        label="Workgroups"
                        value={stats?.workgroups ?? "—"}
                        testId="stat-tile-workgroups"
                    />
                    {/* iter-62: Claude-spend running total (green box request). */}
                    <StatTile
                        icon={BrainCircuit}
                        label="Claude spend"
                        value={
                            typeof stats?.claude_cost_usd === "number"
                                ? `$${stats.claude_cost_usd.toFixed(2)}`
                                : "—"
                        }
                        testId="stat-tile-claude-cost"
                    />
                </section>

                {/* Tabs + search */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    {["users", "projects", "workgroups", "branding", "integrations", "subscriptions", "security", "prompts", "keywords", "errors", "display", "website", "settings", "future"].map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => setTab(t)}
                            data-testid={`admin-tab-${t}`}
                            className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm border transition-colors ${
                                tab === t
                                    ? "bg-[#007AFF]/15 border-[#00A3FF] text-white"
                                    : "border-[#2A3655] text-slate-300 hover:text-white hover:border-[#00A3FF]/60"
                            }`}
                        >
                            {t}
                        </button>
                    ))}
                    <div className="ml-auto relative">
                        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search…"
                            data-testid="admin-search"
                            className="pl-7 pr-3 py-1.5 bg-[#0A1128] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm text-sm text-white outline-none w-56"
                        />
                    </div>
                </div>

                {loadingData ? (
                    <div className="text-sm text-slate-500" data-testid="admin-data-loading">
                        Loading…
                    </div>
                ) : tab === "users" ? (
                    <>
                        {user.role === "superadmin" && <ReviewerToggleCard />}
                        <UsersTable
                            users={filteredUsers}
                            onImpersonate={onImpersonate}
                            onReload={async () => {
                                const u = await adminUsers();
                                setUsers(u);
                            }}
                        />
                    </>
                ) : tab === "projects" ? (
                    <ProjectsTable projects={filteredProjects} />
                ) : tab === "branding" ? (
                    <BrandingSection />
                ) : tab === "integrations" ? (
                    <IntegrationsSection />
                ) : tab === "subscriptions" ? (
                    <SubscriptionsTab />
                ) : tab === "security" ? (
                    <SecurityTab isReviewer={user.role === "reviewer"} />
                ) : tab === "prompts" ? (
                    <>
                        <PricingStrategiesSection />
                        <TemplatesInjectionPromptSection />
                    </>
                ) : tab === "errors" ? (
                    <ClientErrorsSection />
                ) : tab === "keywords" ? (
                    <KeywordsTab />
                ) : tab === "workgroups" ? (
                    <WorkgroupsTab user={user} />
                ) : tab === "display" ? (
                    <DisplayTab />
                ) : tab === "website" ? (
                    <WebsiteTab />
                ) : tab === "settings" ? (
                    <SettingsTab />
                ) : (
                    <FutureTab />
                )}
            </main>
        </div>
    );
}

function StatTile({ icon: Icon, label, value, testId }) {
    return (
        <div
            className="rounded-sm border border-[#2A3655] bg-[#0F1B3A] p-3"
            data-testid={testId}
        >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-400 font-bold">
                <Icon className="w-3 h-3 text-[#00A3FF]" />
                {label}
            </div>
            <div className="text-2xl font-black tracking-tight text-white mt-1 tabular-nums">
                {value}
            </div>
        </div>
    );
}

function ReviewerToggleCard() {
    const [enabled, setEnabled] = useState(true);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const s = await adminGetReviewerStatus();
                setEnabled(!!s.enabled);
            } catch (e) {
                toast.error(
                    e?.response?.data?.detail || "Couldn't load reviewer status",
                );
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const flip = async () => {
        const nextEnabled = !enabled;
        if (
            !nextEnabled &&
            !window.confirm(
                "Disable the app-store reviewer account?\n\nThe reviewer will be immediately signed out and won't be able to log back in until you re-enable this.",
            )
        ) {
            return;
        }
        setBusy(true);
        try {
            const s = await adminSetReviewerStatus(nextEnabled);
            setEnabled(!!s.enabled);
            toast.success(
                nextEnabled
                    ? "Reviewer account enabled"
                    : "Reviewer account disabled",
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Couldn't update reviewer status",
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            className="mb-6 rounded-sm border border-[#2A3655] bg-[#0F1B3A] p-4 flex flex-wrap items-center justify-between gap-3"
            data-testid="reviewer-toggle-card"
        >
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-rose-400 font-bold">
                        Reviewer account
                    </span>
                    {!loading && (
                        <span
                            className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${
                                enabled
                                    ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                                    : "border-rose-500/60 text-rose-300 bg-rose-500/10"
                            }`}
                            data-testid="reviewer-toggle-status"
                        >
                            {enabled ? "Enabled" : "Disabled"}
                        </span>
                    )}
                </div>
                <p className="mt-1 text-[12px] text-slate-400 max-w-prose">
                    Hardcoded login <code className="text-[11px] bg-[#0A1128] px-1 py-0.5 rounded border border-[#2A3655]">reviewer</code> used by App Store / Play Store reviewers. Turn it off once review is complete — they'll be signed out instantly.
                </p>
            </div>
            <button
                type="button"
                onClick={flip}
                disabled={loading || busy}
                data-testid="reviewer-toggle-button"
                className={`px-4 py-2 text-sm font-bold rounded-sm transition-colors disabled:opacity-50 ${
                    enabled
                        ? "bg-rose-600 hover:bg-rose-500 text-white"
                        : "bg-emerald-600 hover:bg-emerald-500 text-white"
                }`}
            >
                {busy ? "Saving…" : enabled ? "Disable reviewer" : "Enable reviewer"}
            </button>
        </section>
    );
}

